/**
 * Loss Runs tabbed card — the 3-tab container from the Figma:
 *   Loss Runs · Annual Loss Summary · Loss Ratio Analysis
 *
 * Follows the Regulatory section's pattern: atlas `Tabs` is used purely
 * as a controlled tab strip (`Tabs` + `Tabs.List`, no `TabPanel`) and
 * the panel body is rendered with plain conditionals. Mobile collapses
 * the strip to a Select.
 *
 * Tab data (all via `useLossRuns`):
 *   - Loss Runs (OR-1174) — the staged, editable claims table.
 *   - Annual Loss Summary (OR-1175) — read-only, backend `/summary`.
 *   - Loss Ratio Analysis (OR-1176) — read-only, backend `/summary`.
 *
 * "+ Add Row" sits in the card header and shows only on the Loss Runs
 * tab — the two summary tabs are read-only.
 */
import { useState } from "react";
import type { Key } from "react-aria-components";
import { Button, Select, Tabs } from "@tricura/atlas-ui";
import { useLossRuns } from "../../../../hooks/use-loss-runs";
import { AnnualLossSummaryTable } from "./annual-loss-summary-table";
import { LossRatioAnalysisTable } from "./loss-ratio-analysis-table";
import { LossRunsTable } from "./loss-runs-table";

const TAB_ITEMS = [
    { id: "loss-runs", label: "Loss Runs", autotest: "loss-runs-tabs-loss-runs-tab" },
    { id: "annual", label: "Annual Loss Summary", autotest: "loss-runs-tabs-annual-loss-summary-tab" },
    { id: "ratio", label: "Loss Ratio Analysis", autotest: "loss-runs-tabs-loss-ratio-analysis-tab" },
];

interface LossRunsTabsProps {
    submissionId: string | undefined;
    /** Set once Save & Continue is attempted — forwarded to the table
     *  so its cells flip into the error state. */
    showErrors: boolean;
}

export function LossRunsTabs({ submissionId, showErrors }: LossRunsTabsProps) {
    const [tab, setTab] = useState("loss-runs");
    // Shared staged SoT — the Loss Runs table consumes the same hook
    // instance's data via the module-level pubsub, so "Add Row" stays in sync.
    const { create } = useLossRuns(submissionId);

    const selectTab = (key: Key | null) => {
        if (key !== null) setTab(String(key));
    };

    /** Only the editable Loss Runs tab gets "+ Add Row". */
    const showAddRow = tab === "loss-runs";

    return (
        <section className="border-secondary bg-primary shadow-xs flex flex-col overflow-hidden rounded-xl border lg:min-h-0 lg:flex-1">
            <header className="border-secondary flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3 sm:px-6 sm:py-4">
                {/* Desktop: pill tab strip */}
                <div className="hidden md:block">
                    <Tabs selectedKey={tab} onSelectionChange={selectTab} className="w-max">
                        {/* `button-minimal` matches the Regulatory section's tab strip
                          * (Survey / Acuity) — the established design for in-card tabs. */}
                        <Tabs.List type="button-minimal" size="sm" aria-label="Loss Runs views">
                            {TAB_ITEMS.map((item) => (
                                <Tabs.Item key={item.id} id={item.id} label={item.label} data-autotest={item.autotest} />
                            ))}
                        </Tabs.List>
                    </Tabs>
                </div>
                {/* Mobile: the same selector collapsed into a dropdown */}
                <div className="flex-1 md:hidden">
                    <Select
                        size="sm"
                        aria-label="Loss Runs view"
                        className="w-full"
                        selectedKey={tab}
                        onSelectionChange={selectTab}
                        items={TAB_ITEMS}
                        data-autotest="loss-runs-tabs-mobile-select"
                    >
                        {(item) => (
                            <Select.Item id={item.id} data-autotest={`${TAB_ITEMS.find((t) => t.id === item.id)?.autotest}-option`}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>

                {showAddRow && (
                    <Button color="link-gray" size="sm" onClick={() => void create()} data-autotest="loss-runs-add-row-button">
                        + Add Row
                    </Button>
                )}
            </header>

            {tab === "loss-runs" && <LossRunsTable submissionId={submissionId} showErrors={showErrors}/>}
            {tab === "annual" && <AnnualLossSummaryTable submissionId={submissionId}/>}
            {tab === "ratio" && <LossRatioAnalysisTable submissionId={submissionId}/>}
        </section>
    );
}
